import {
  AuthField,
  AuthHero,
  AuthModeSelector,
  OAuthButton,
  PrimaryAuthButton,
  SignedInPanel,
  StatusMessage,
} from "@/components/auth/AuthFlowComponents"
import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { Box } from "@/components/ui/box"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  type AuthFlowMode,
  type AuthValidationErrors,
  hasAuthValidationErrors,
  isE2EAuthFlowStubEnabled,
  validateAuthFlow,
} from "@/services/authHelpers"
import { router, useLocalSearchParams } from "expo-router"
import { ShieldCheck } from "lucide-react-native"
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Animated, Platform, TouchableOpacity } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const modeOptions: { mode: AuthFlowMode; label: string }[] = [
  { mode: "signin", label: "Log in" },
  { mode: "signup", label: "Sign up" },
  { mode: "magic", label: "Magic link" },
]

type E2EAuthAction =
  | "validate"
  | "signupSuccess"
  | "loginSuccess"
  | "magicSent"
  | "resetSent"
  | "googleSuccess"
  | "signOut"

const modeCopy: Record<AuthFlowMode, { title: string; subtitle: string; submit: string }> = {
  signin: {
    title: "Log in to sync your ProofTree",
    subtitle:
      "Use your Anuka account to keep proofs, tasks, Passport progress, and EQ history synced.",
    submit: "Log In",
  },
  signup: {
    title: "Create your EMO.coach account",
    subtitle: "Start with local coaching today, then sync proof submissions and Passport progress.",
    submit: "Create Account",
  },
  magic: {
    title: "Send a magic link",
    subtitle: "Use email-only sign-in when you want a passwordless path back to your Passport.",
    submit: "Send Magic Link",
  },
  reset: {
    title: "Reset your password",
    subtitle:
      "Send a secure reset link to your email and return here after choosing a new password.",
    submit: "Send Reset Link",
  },
}

function getInitialMode(value: unknown): AuthFlowMode {
  return value === "signup" || value === "magic" || value === "reset" || value === "signin"
    ? value
    : "signin"
}

function firstQueryValue(value: string | string[] | null | undefined) {
  const firstValue = Array.isArray(value) ? value[0] : value
  return firstValue?.trim() ? firstValue.trim() : undefined
}

export default function AuthScreen() {
  const auth = useAuth()
  const params = useLocalSearchParams<{ e2eAction?: string; e2eNonce?: string; mode?: string }>()
  const [mode, setMode] = useState<AuthFlowMode>(() => getInitialMode(params.mode))
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [displayName, setDisplayName] = useState("")
  const [errors, setErrors] = useState<AuthValidationErrors>({})
  const [submittedMode, setSubmittedMode] = useState<AuthFlowMode | null>(null)
  const handledE2EAction = useRef<string | null>(null)
  const scrollY = useRef(new Animated.Value(0)).current
  const testStubAvailable = isE2EAuthFlowStubEnabled()
  const canUseAuth = auth.configured || testStubAvailable

  const copy = modeCopy[mode]
  const messageTone =
    auth.message.includes("Account created") ||
    auth.message.includes("Magic link sent") ||
    auth.message.includes("Test auth stub")
      ? "success"
      : "info"

  const submitLabel = useMemo(() => {
    if (auth.actionLoading && submittedMode === mode) return "Working..."
    return copy.submit
  }, [auth.actionLoading, copy.submit, mode, submittedMode])

  useEffect(() => {
    const nextMode = getInitialMode(params.mode)
    setMode(nextMode)
  }, [params.mode])

  useEffect(() => {
    const searchParams =
      Platform.OS === "web" && typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null
    const e2eAction =
      firstQueryValue(params.e2eAction) ?? searchParams?.get("e2eAction") ?? undefined
    const e2eNonce = firstQueryValue(params.e2eNonce) ?? searchParams?.get("e2eNonce") ?? undefined

    if (!testStubAvailable || !e2eAction || handledE2EAction.current === e2eNonce) {
      return
    }

    const action = e2eAction as E2EAuthAction
    handledE2EAction.current = e2eNonce ?? action

    if (action === "validate") {
      setErrors(validateAuthFlow({ mode, email, password, confirmPassword }))
      return
    }

    if (action === "signOut") {
      void auth.signOut()
      return
    }

    setSubmittedMode(mode)
    if (action === "signupSuccess") {
      void auth.signUpWithPassword(
        email || "browser.signup@anuka.pro",
        password || "testanuka",
        displayName,
      )
      return
    }

    if (action === "loginSuccess") {
      void auth.signInWithPassword(email || "browser.login@anuka.pro", password || "testanuka")
      return
    }

    if (action === "magicSent") {
      void auth.sendMagicLink(email || "browser.magic@anuka.pro")
      return
    }

    if (action === "resetSent") {
      void auth.sendPasswordReset(email || "browser.reset@anuka.pro")
      return
    }

    if (action === "googleSuccess") {
      void auth.signInWithOAuth("google")
    }
  }, [
    auth,
    confirmPassword,
    displayName,
    email,
    mode,
    params.e2eAction,
    params.e2eNonce,
    password,
    testStubAvailable,
  ])

  function updateMode(nextMode: AuthFlowMode) {
    setMode(nextMode)
    setErrors({})
    auth.clearMessage()
  }

  async function submit() {
    const nextErrors = validateAuthFlow({ mode, email, password, confirmPassword })
    setErrors(nextErrors)
    if (hasAuthValidationErrors(nextErrors)) return

    setSubmittedMode(mode)
    if (mode === "signup") {
      await auth.signUpWithPassword(email, password, displayName)
      return
    }

    if (mode === "magic") {
      await auth.sendMagicLink(email)
      return
    }

    if (mode === "reset") {
      await auth.sendPasswordReset(email)
      return
    }

    await auth.signInWithPassword(email, password)
  }

  if (auth.session) {
    const display =
      auth.profile?.display_name ?? auth.user?.email?.split("@")[0] ?? "EMO.coach builder"

    return (
      <AuthScreenFrame scrollY={scrollY} subtitle="Account synced">
        <SignedInPanel
          actionLoading={auth.actionLoading}
          displayName={display}
          email={auth.user?.email}
          message={auth.message}
          onOpenPassport={() => router.replace("/(tabs)/profile")}
          onSignOut={auth.signOut}
        />
      </AuthScreenFrame>
    )
  }

  return (
    <AuthScreenFrame scrollY={scrollY} subtitle={copy.title}>
      <VStack className="gap-5" testID="auth-flow">
        <AuthHero title={copy.title} subtitle={copy.subtitle} />

        {!auth.configured ? (
          <StatusMessage
            tone="warning"
            message="Supabase env values are missing. This screen stays visible, but production sign-in is disabled until EXPO_PUBLIC_SUPABASE_URL and EXPO_PUBLIC_SUPABASE_ANON_KEY are set."
            testID="auth-config-warning"
          />
        ) : null}

        {testStubAvailable ? (
          <StatusMessage
            tone="info"
            message="Test auth stub is enabled for browser automation. This path is disabled outside explicit E2E development flags."
            testID="auth-test-stub-banner"
          />
        ) : null}

        {auth.message ? (
          <StatusMessage tone={messageTone} message={auth.message} testID="auth-status-message" />
        ) : null}

        <AuthModeSelector mode={mode} options={modeOptions} onChange={updateMode} />

        <VStack className="gap-3">
          {mode === "signup" ? (
            <AuthField
              icon="user"
              label="Display name"
              placeholder="Sergey"
              testID="auth-display-name-input"
              value={displayName}
              onChangeText={setDisplayName}
            />
          ) : null}
          <AuthField
            autoCapitalize="none"
            error={errors.email}
            icon="email"
            keyboardType="email-address"
            label="Email"
            placeholder="you@example.com"
            testID="auth-email-input"
            value={email}
            onChangeText={setEmail}
          />
          {mode !== "magic" && mode !== "reset" ? (
            <AuthField
              error={errors.password}
              label="Password"
              placeholder="At least 6 characters"
              secureTextEntry
              testID="auth-password-input"
              value={password}
              onChangeText={setPassword}
            />
          ) : null}
          {mode === "signup" ? (
            <AuthField
              error={errors.confirmPassword}
              label="Confirm password"
              placeholder="Repeat your password"
              secureTextEntry
              testID="auth-confirm-password-input"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
            />
          ) : null}
        </VStack>

        {mode === "signin" ? (
          <TouchableOpacity
            accessibilityRole="button"
            className="self-center px-4 py-2"
            testID="auth-reset-link"
            onPress={() => updateMode("reset")}
          >
            <Text className="font-bold" style={{ color: Colors.dark.primary }}>
              Forgot password?
            </Text>
          </TouchableOpacity>
        ) : null}

        <PrimaryAuthButton
          disabled={!canUseAuth || auth.actionLoading}
          testID="auth-submit"
          onPress={submit}
        >
          {submitLabel}
        </PrimaryAuthButton>

        <OAuthSection
          disabled={!canUseAuth || auth.actionLoading}
          onGoogle={() => auth.signInWithOAuth("google")}
          onLinkedIn={() => auth.signInWithOAuth("linkedin_oidc")}
          onApple={() => auth.signInWithOAuth("apple")}
        />
      </VStack>
    </AuthScreenFrame>
  )
}

function AuthScreenFrame({
  children,
  scrollY,
  subtitle,
}: {
  children: ReactNode
  scrollY: Animated.Value
  subtitle: string
}) {
  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        backFallbackHref="/(tabs)/profile"
        eyebrow="Anuka Auth"
        scrollY={scrollY}
        subtitle={subtitle}
        title="Account"
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 56 : 36,
        }}
        keyboardShouldPersistTaps="handled"
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        {children}
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function OAuthSection({
  disabled,
  onGoogle,
  onLinkedIn,
  onApple,
}: {
  disabled?: boolean
  onGoogle: () => void
  onLinkedIn: () => void
  onApple: () => void
}) {
  return (
    <VStack className="gap-3" testID="auth-oauth-section">
      <HStack className="items-center gap-2">
        <Box
          className="h-8 w-8 items-center justify-center rounded-full"
          style={{ backgroundColor: `${Colors.dark.primary}1F` }}
        >
          <ShieldCheck color={Colors.dark.primary} size={16} />
        </Box>
        <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
          Google can use the configured Supabase OAuth app on web; LinkedIn and Apple keep their
          provider guardrails.
        </Text>
      </HStack>
      <HStack className="gap-3">
        <OAuthButton label="Google" disabled={disabled} testID="auth-google" onPress={onGoogle} />
        <OAuthButton
          label="LinkedIn"
          disabled={disabled}
          secondary
          testID="auth-linkedin"
          onPress={onLinkedIn}
        />
      </HStack>
      <OAuthButton
        label="Apple"
        disabled={disabled}
        secondary
        testID="auth-apple"
        onPress={onApple}
      />
    </VStack>
  )
}
